import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import {
  AppHeader,
  UserProfilePopover,
  EnvironmentInfoPopover,
} from '@web-config-app/core-react-ui';
import type { AppHeaderItem } from '@web-config-app/core-react-ui';
import {
  useConfigApp,
  useAuthoringEnvironments,
  useAuthoringEnvironmentInfo,
  useUserInfo,
  useHasPermissionsHelpers,
} from '@web-config-app/core-react';
import { buildTenantPath, checkModulePermissions } from '@web-config-app/core';
import type { EnvironmentKey } from '@web-config-app/core';
import { TenantSwitcherContainer } from '../tenant-switcher/tenant-switcher.container';

interface AppHeaderContainerProps {
  currentEnvironment?: EnvironmentKey;
}

export const AppHeaderContainer = ({
  currentEnvironment,
}: AppHeaderContainerProps) => {
  const { tenantId, modules = [] } = useConfigApp();
  const { baseAuthoringEnvironment, multiEnvironmentAuthoringEnabled } =
    useAuthoringEnvironments();
  const authoringEnvironmentInfo =
    useAuthoringEnvironmentInfo(currentEnvironment);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { userInfo, isUserInfoLoaded } = useUserInfo();
  const userNameLabel = `${userInfo?.firstName} ${userInfo?.lastName}`.trim();
  const { hasAllPermissions } = useHasPermissionsHelpers({
    environmentKey: baseAuthoringEnvironment.environmentKey,
    tenantId,
    isAuthenticated: true,
  });

  const navigationItems: AppHeaderItem[] = React.useMemo(
    () =>
      modules
        .filter(
          (tabModule) =>
            (tabModule.type === 'entity' ||
              tabModule.type === 'componentPage' ||
              tabModule.type === 'schemaPage' ||
              (tabModule.modules && tabModule.modules.length > 0)) &&
            checkModulePermissions(tabModule.permissions, hasAllPermissions),
        )
        .map((tabModule) => ({
          id: tabModule.id,
          name: formatMessage({ id: tabModule.labelKey }, { count: 2 }),
          path: buildTenantPath(tenantId, tabModule.id),
          icon: tabModule.icon,
        })),
    // isUserInfoLoaded triggers recomputation so isAdminUser() (called inside
    // checkModulePermissions) re-evaluates after user info is fetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modules, formatMessage, tenantId, isUserInfoLoaded, hasAllPermissions],
  );

  return (
    <AppHeader
      items={navigationItems}
      tenantSwitcher={<TenantSwitcherContainer />}
    >
      <EnvironmentInfoPopover
        environment={authoringEnvironmentInfo}
        multiEnvironmentAuthoringEnabled={multiEnvironmentAuthoringEnabled}
      />
      <UserProfilePopover
        triggerLabel={formatMessage({ id: 'PROFILE' })}
        userName={userNameLabel}
        userEmail={userInfo?.email}
        ctaLabel={formatMessage({ id: 'LOG_OUT' })}
        ctaAction={() => navigate('/sign-out')}
      />
    </AppHeader>
  );
};
